import { useState } from 'react';
import { Button, Chip, IconButton, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { type GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { PageHeader } from '../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ErrorState } from '../../../shared/components/ErrorState';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { listPurchaseRequests, purchaseRequestsApi } from '../api';
import { PurchaseRequestFormDialog } from '../PurchaseRequestFormDialog';
import type { PurchaseRequestDto, PurchaseRequestListItem, PurchaseRequestStatus } from '../types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'dataCadastro',
  sortOrder: 'desc',
};

function formatMoney(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return '—';
  const n = Number(String(raw).replace(',', '.'));
  if (!Number.isFinite(n)) return raw as string;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function PurchaseRequestsListPage(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const {
    query,
    setQuery,
    filters,
    patchFilters,
    clear,
  } = usePersistedGridState('requisicoes-compra', DEFAULT_QUERY, {
    status: '' as PurchaseRequestStatus | '',
  });
  const statusFiltro = filters.status;
  const setStatusFiltro = (status: PurchaseRequestStatus | '') => patchFilters({ status });
  const [editing, setEditing] = useState<PurchaseRequestDto | null | 'new'>(null);

  const listQuery = useQuery({
    queryKey: ['purchase-requests', 'list', query, statusFiltro],
    queryFn: () => listPurchaseRequests(query, { status: statusFiltro || undefined }),
    placeholderData: (previous) => previous,
  });

  async function openEdit(row: PurchaseRequestListItem): Promise<void> {
    try {
      setEditing(await purchaseRequestsApi.get(row.id));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<PurchaseRequestListItem>[] = [
    {
      field: 'referencia',
      headerName: t('features.requisicoesCompra.columns.referencia'),
      width: 160,
    },
    {
      field: 'clienteNome',
      headerName: t('features.requisicoesCompra.columns.cliente'),
      flex: 1,
      minWidth: 200,
      sortable: false,
      valueFormatter: (value: string | null) => value ?? '—',
    },
    {
      field: 'valorInicial',
      headerName: t('features.requisicoesCompra.columns.valorInicial'),
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: string) => formatMoney(value),
    },
    {
      field: 'saldo',
      headerName: t('features.requisicoesCompra.columns.saldo'),
      width: 140,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      valueFormatter: (value: string) => formatMoney(value),
    },
    {
      field: 'statusSaldo',
      headerName: t('features.requisicoesCompra.columns.statusSaldo'),
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const code = params.value as string;
        if (!code) return '—';
        const label = t(`features.requisicoesCompra.statusSaldoLabel.${code}`, {
          defaultValue: code,
        });
        return (
          <Tooltip title={label}>
            <span>{code}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'status',
      headerName: t('features.requisicoesCompra.columns.status'),
      width: 110,
      sortable: false,
      renderCell: (params) => {
        const status = params.value as string;
        const color = status === 'ATV' ? 'success' : status === 'ENC' ? 'default' : 'default';
        const variant = status === 'CAN' ? 'outlined' : 'filled';
        return (
          <Chip
            label={t(`features.requisicoesCompra.statusLabel.${status}`, {
              defaultValue: status,
            })}
            size="small"
            color={color}
            variant={variant}
          />
        );
      },
    },
    {
      field: 'criadoEm',
      headerName: t('features.requisicoesCompra.columns.criadoEm'),
      width: 150,
      valueFormatter: (value: string) => (value ? dayjs(value).format('DD/MM/YYYY') : '—'),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('features.requisicoesCompra.actions.extratoTooltip')}>
            <IconButton
              size="small"
              aria-label={t('features.requisicoesCompra.actions.extrato', {
                referencia: params.row.referencia,
              })}
              onClick={() => navigate(`/clientes/requisicoes-compra/${params.row.id}`)}
            >
              <ReceiptLongIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.requisicoesCompra.actions.edit', {
                referencia: params.row.referencia,
              })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <>
        <PageHeader title={t('menu.clientsPurchaseRequests')} />
        <ErrorState
          description={describeApiError(listQuery.error)}
          onRetry={() => listQuery.refetch()}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('menu.clientsPurchaseRequests')}
        breadcrumbs={[{ label: t('menu.clients') }, { label: t('menu.clientsPurchaseRequests') }]}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
            {t('features.requisicoesCompra.actions.new')}
          </Button>
        }
      />

      <DataTable<PurchaseRequestListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.requisicoesCompra.searchPlaceholder')}
        filters={
          <TextField
            select
            size="small"
            label={t('features.requisicoesCompra.filtros.status')}
            value={statusFiltro}
            onChange={(e) => setStatusFiltro(e.target.value as PurchaseRequestStatus | '')}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t('features.requisicoesCompra.filtros.statusTodos')}</MenuItem>
            <MenuItem value="ATV">{t('features.requisicoesCompra.statusLabel.ATV')}</MenuItem>
            <MenuItem value="ENC">{t('features.requisicoesCompra.statusLabel.ENC')}</MenuItem>
            <MenuItem value="CAN">{t('features.requisicoesCompra.statusLabel.CAN')}</MenuItem>
          </TextField>
        }
      />

      <PurchaseRequestFormDialog
        open={editing !== null}
        purchaseRequest={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
    </>
  );
}
